import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { KeyValueList } from '../components/KeyValueList';
import { adminApi, ApiError, type InstagramStatus } from '../lib/api';
import { copy, formatYesNo } from '../lib/copy';
import { cn, formatDate, formatNumber } from '../lib/utils';
import { ErrorState, KpiCard, LoadingState, PageHeader } from '../components/ui';

type Tab = 'overview' | 'settings' | 'operations' | 'setup' | 'privacy';

function formatAccountStatus(status: string | undefined): string {
  if (status === 'active') return 'Активен';
  if (status === 'token_invalid') return 'Токен недействителен';
  return status ?? copy.common.notAvailable;
}

function formatWebhookEvents(events: Record<string, number> | undefined): string {
  if (!events) return copy.common.notAvailable;
  const total = Object.values(events).reduce((sum, value) => sum + value, 0);
  if (total === 0) return 'Событий нет';
  return Object.entries(events)
    .filter(([, value]) => value > 0)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
}

function deliveryTotal(
  bucket: InstagramStatus['replyDelivery']['last24Hours'] | undefined,
): number {
  if (!bucket) return 0;
  return (
    bucket.public.sent +
    bucket.private.sent +
    bucket.dm.sent +
    bucket.public.failed +
    bucket.private.failed +
    bucket.dm.failed +
    bucket.public.blocked +
    bucket.private.blocked +
    bucket.dm.blocked
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-pill px-4 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-ops-accent-soft text-ops-text'
          : 'text-ops-muted hover:bg-ops-surface-muted hover:text-ops-text',
      )}
    >
      {children}
    </button>
  );
}

function FlagRow({ label, value }: { label: string; value: boolean | string | number }) {
  const display =
    typeof value === 'boolean'
      ? formatYesNo(value)
      : typeof value === 'number'
        ? formatNumber(value)
        : value || copy.common.notAvailable;
  return (
    <div className="flex items-center justify-between gap-4 border-b border-ops-border/50 py-3 text-sm">
      <span className="text-ops-muted">{label}</span>
      <span className="text-right text-ops-text">{display}</span>
    </div>
  );
}

export function InstagramPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [mediaId, setMediaId] = useState('');
  const [backfillSend, setBackfillSend] = useState(false);
  const [backfillAfter, setBackfillAfter] = useState('');
  const [backfillMaxPages, setBackfillMaxPages] = useState('');

  const [commentId, setCommentId] = useState('');
  const [replyForce, setReplyForce] = useState(false);

  const [subjectCommentId, setSubjectCommentId] = useState('');
  const [subjectDirectMessageId, setSubjectDirectMessageId] = useState('');
  const [subjectCommenterId, setSubjectCommenterId] = useState('');
  const [subjectUsername, setSubjectUsername] = useState('');
  const [subjectReport, setSubjectReport] = useState<Record<string, unknown> | null>(null);

  const setupQuery = useQuery({
    queryKey: ['instagram', 'setup'],
    queryFn: adminApi.instagramSetup,
  });

  const statusQuery = useQuery({
    queryKey: ['instagram', 'status'],
    queryFn: adminApi.instagramStatus,
    enabled: setupQuery.data?.configured === true,
    refetchInterval: tab === 'overview' ? 30_000 : false,
  });

  const healthQuery = useQuery({
    queryKey: ['instagram', 'health'],
    queryFn: adminApi.instagramHealth,
    enabled: setupQuery.data?.configured === true && tab === 'setup',
  });

  const backfillMutation = useMutation({
    mutationFn: () =>
      adminApi.instagramBackfillMedia(mediaId.trim(), {
        send: backfillSend ? 'true' : undefined,
        after: backfillAfter.trim() || undefined,
        maxPages: backfillMaxPages.trim() || undefined,
      }),
    onSuccess: (result) => {
      setActionError(null);
      setActionMessage(copy.instagram.backfillSuccess);
      setSubjectReport(result);
      queryClient.invalidateQueries({ queryKey: ['instagram', 'status'] });
    },
    onError: (error: unknown) => {
      setActionMessage(null);
      setActionError(
        error instanceof ApiError ? error.message : copy.instagram.actionFailed,
      );
    },
  });

  const replyMutation = useMutation({
    mutationFn: () =>
      adminApi.instagramReplyComment(commentId.trim(), replyForce ? 'true' : undefined),
    onSuccess: (result) => {
      setActionError(null);
      setActionMessage(copy.instagram.replySuccess);
      setSubjectReport(result);
      queryClient.invalidateQueries({ queryKey: ['instagram', 'status'] });
    },
    onError: (error: unknown) => {
      setActionMessage(null);
      setActionError(
        error instanceof ApiError ? error.message : copy.instagram.actionFailed,
      );
    },
  });

  const lookupMutation = useMutation({
    mutationFn: () =>
      adminApi.instagramDataSubjectLookup({
        commentId: subjectCommentId.trim() || undefined,
        directMessageId: subjectDirectMessageId.trim() || undefined,
        commenterId: subjectCommenterId.trim() || undefined,
        username: subjectUsername.trim() || undefined,
      }),
    onSuccess: (result) => {
      setActionError(null);
      setActionMessage(copy.instagram.lookupSuccess);
      setSubjectReport(result);
    },
    onError: (error: unknown) => {
      setActionMessage(null);
      setActionError(
        error instanceof ApiError ? error.message : copy.instagram.actionFailed,
      );
    },
  });

  const redactMutation = useMutation({
    mutationFn: () =>
      adminApi.instagramDataSubjectRedact({
        commentId: subjectCommentId.trim() || undefined,
        directMessageId: subjectDirectMessageId.trim() || undefined,
        commenterId: subjectCommenterId.trim() || undefined,
        username: subjectUsername.trim() || undefined,
      }),
    onSuccess: (result) => {
      setActionError(null);
      setActionMessage(copy.instagram.redactSuccess);
      setSubjectReport(result);
      queryClient.invalidateQueries({ queryKey: ['instagram', 'status'] });
    },
    onError: (error: unknown) => {
      setActionMessage(null);
      setActionError(
        error instanceof ApiError ? error.message : copy.instagram.actionFailed,
      );
    },
  });

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: copy.instagram.tabs.overview },
    { id: 'settings', label: copy.instagram.tabs.settings },
    { id: 'operations', label: copy.instagram.tabs.operations },
    { id: 'setup', label: copy.instagram.tabs.setup },
    { id: 'privacy', label: copy.instagram.tabs.privacy },
  ];

  const status = statusQuery.data;
  const config = status?.config;

  const handleBackfill = (event: FormEvent) => {
    event.preventDefault();
    setActionMessage(null);
    setActionError(null);
    if (!mediaId.trim()) {
      setActionError(copy.instagram.mediaIdRequired);
      return;
    }
    backfillMutation.mutate();
  };

  const handleReply = (event: FormEvent) => {
    event.preventDefault();
    setActionMessage(null);
    setActionError(null);
    if (!commentId.trim()) {
      setActionError(copy.instagram.commentIdRequired);
      return;
    }
    replyMutation.mutate();
  };

  const handleLookup = (event: FormEvent) => {
    event.preventDefault();
    setActionMessage(null);
    setActionError(null);
    lookupMutation.mutate();
  };

  const handleRedact = () => {
    setActionMessage(null);
    setActionError(null);
    if (
      !subjectCommentId.trim() &&
      !subjectDirectMessageId.trim() &&
      !subjectCommenterId.trim() &&
      !subjectUsername.trim()
    ) {
      setActionError(copy.instagram.subjectSelectorRequired);
      return;
    }
    if (!window.confirm(copy.instagram.redactConfirm)) {
      return;
    }
    redactMutation.mutate();
  };

  return (
    <div>
      <PageHeader
        title={copy.instagram.title}
        description={copy.instagram.description}
        actions={
          setupQuery.data?.configured ? (
            <button
              type="button"
              className="ops-btn-secondary"
              onClick={() => {
                void statusQuery.refetch();
                void healthQuery.refetch();
              }}
            >
              <RefreshCw className="mr-2 inline h-4 w-4" />
              {copy.instagram.refresh}
            </button>
          ) : null
        }
      />

      <div className="mb-6 flex flex-wrap gap-2">
        {tabs.map((item) => (
          <TabButton key={item.id} active={tab === item.id} onClick={() => setTab(item.id)}>
            {item.label}
          </TabButton>
        ))}
      </div>

      {setupQuery.isLoading ? <LoadingState label={copy.instagram.loadingSetup} /> : null}

      {setupQuery.isError ? (
        <ErrorState
          message={copy.instagram.errors.loadSetup}
          onRetry={() => setupQuery.refetch()}
        />
      ) : null}

      {setupQuery.data && !setupQuery.data.configured ? (
        <div className="ops-card border-ops-warning/30 bg-ops-warning-soft">
          <h2 className="text-sm font-semibold text-ops-text">{copy.instagram.notConfiguredTitle}</h2>
          <p className="mt-2 text-sm text-ops-muted">{copy.instagram.notConfiguredBody}</p>
          <pre className="mt-4 overflow-x-auto rounded-xl bg-ops-surface p-4 text-xs text-ops-secondary">
            {`INSTAGRAM_BOT_ADMIN_BASE_URL=https://instagram-bot.prismahub-bot-ig.workers.dev
INSTAGRAM_BOT_ADMIN_API_KEY=<ADMIN_API_KEY из ig-bot>`}
          </pre>
        </div>
      ) : null}

      {setupQuery.data?.configured && tab === 'overview' ? (
        <>
          {statusQuery.isLoading ? <LoadingState label={copy.instagram.loadingStatus} /> : null}
          {statusQuery.isError ? (
            <ErrorState
              message={copy.instagram.errors.loadStatus}
              onRetry={() => statusQuery.refetch()}
            />
          ) : null}
          {status ? (
            <>
              <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                  label={copy.instagram.accountStatus}
                  value={formatAccountStatus(status.accountStatus)}
                />
                <KpiCard
                  label={copy.instagram.webhookEvents}
                  value={formatWebhookEvents(status.webhookEvents)}
                />
                <KpiCard
                  label={copy.instagram.replyJobsDue}
                  value={formatNumber(status.replyJobs.due)}
                  hint={
                    status.replyJobs.oldestDueAgeSeconds
                      ? `${copy.instagram.oldestDue}: ${status.replyJobs.oldestDueAgeSeconds} с`
                      : undefined
                  }
                />
                <KpiCard
                  label={copy.instagram.delivery24h}
                  value={formatNumber(deliveryTotal(status.replyDelivery.last24Hours))}
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="ops-card">
                  <h2 className="mb-3 text-sm font-semibold">{copy.instagram.jobsTitle}</h2>
                  <KeyValueList
                    items={Object.entries(status.replyJobs.counts).map(([key, value]) => ({
                      label:
                        copy.instagram.jobStatusLabels[
                          key as keyof typeof copy.instagram.jobStatusLabels
                        ] ?? key,
                      value: formatNumber(value),
                    }))}
                  />
                </div>

                <div className="ops-card">
                  <h2 className="mb-3 text-sm font-semibold">{copy.instagram.deliveryTitle}</h2>
                  {(['public', 'private', 'dm'] as const).map((channel) => (
                    <div key={channel} className="mb-4 last:mb-0">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ops-muted">
                        {copy.instagram.deliveryChannels[channel]}
                      </p>
                      <KeyValueList
                        items={[
                          {
                            label: copy.instagram.deliverySent,
                            value: formatNumber(status.replyDelivery.last24Hours[channel].sent),
                          },
                          {
                            label: copy.instagram.deliveryFailed,
                            value: formatNumber(status.replyDelivery.last24Hours[channel].failed),
                          },
                          {
                            label: copy.instagram.deliveryBlocked,
                            value: formatNumber(status.replyDelivery.last24Hours[channel].blocked),
                          },
                        ]}
                      />
                    </div>
                  ))}
                </div>

                <div className="ops-card">
                  <h2 className="mb-3 text-sm font-semibold">{copy.instagram.topErrorsTitle}</h2>
                  {status.topReplyErrors.length === 0 ? (
                    <p className="text-sm text-ops-muted">{copy.instagram.noErrors}</p>
                  ) : (
                    <div className="space-y-3">
                      {status.topReplyErrors.map((item, index) => (
                        <div
                          key={`${item.type}-${item.lastError ?? 'none'}-${index}`}
                          className="rounded-xl border border-ops-border/60 p-3 text-sm"
                        >
                          <p className="font-medium text-ops-text">
                            {item.type} · {item.count}
                          </p>
                          <p className="mt-1 text-ops-muted">
                            {item.lastError || copy.common.notAvailable}
                          </p>
                          {item.metaCode ? (
                            <p className="mt-1 text-xs text-ops-muted">
                              Meta {item.metaCode}
                              {item.metaSubcode ? ` / ${item.metaSubcode}` : ''}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ops-card">
                  <h2 className="mb-3 text-sm font-semibold">{copy.instagram.reconcilerTitle}</h2>
                  {status.reconciler.lastRun ? (
                    <KeyValueList
                      items={[
                        {
                          label: copy.instagram.reconcilerStarted,
                          value: formatDate(status.reconciler.lastRun.startedAt),
                        },
                        {
                          label: copy.instagram.reconcilerFinished,
                          value: formatDate(status.reconciler.lastRun.finishedAt),
                        },
                        {
                          label: copy.instagram.reconcilerMedia,
                          value: formatNumber(status.reconciler.lastRun.mediaScanned),
                        },
                        {
                          label: copy.instagram.reconcilerComments,
                          value: formatNumber(status.reconciler.lastRun.commentsScanned),
                        },
                        {
                          label: copy.instagram.reconcilerRecovered,
                          value: formatNumber(status.reconciler.lastRun.commentsRecovered),
                        },
                        {
                          label: copy.instagram.reconcilerJobs,
                          value: formatNumber(status.reconciler.lastRun.replyJobsCreated),
                        },
                        {
                          label: copy.instagram.reconcilerErrors,
                          value: formatNumber(status.reconciler.lastRun.errors),
                        },
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-ops-muted">{copy.instagram.reconcilerEmpty}</p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {setupQuery.data?.configured && tab === 'settings' && config ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="ops-card">
            <h2 className="mb-2 text-sm font-semibold">{copy.instagram.connectionTitle}</h2>
            <FlagRow label={copy.instagram.instagramAccount} value={`@${config.instagramUsername ?? ''}`} />
            <FlagRow label={copy.instagram.accountId} value={config.instagramAccountId} />
            <FlagRow label={copy.instagram.publicBaseUrl} value={config.publicBaseUrl ?? ''} />
            <FlagRow label={copy.instagram.graphApiBase} value={config.metaGraphApiBaseUrl} />
            <FlagRow label={copy.instagram.graphApiVersion} value={config.metaGraphApiVersion} />
            <FlagRow label={copy.instagram.hasAccessToken} value={config.hasInstagramAccessToken} />
            <FlagRow label={copy.instagram.hasAppSecret} value={config.hasMetaAppSecret} />
            <FlagRow label={copy.instagram.hasAdminKey} value={config.hasAdminApiKey} />
          </div>

          <div className="ops-card">
            <h2 className="mb-2 text-sm font-semibold">{copy.instagram.flagsTitle}</h2>
            <FlagRow label={copy.instagram.flagBotEnabled} value={config.botEnabled} />
            <FlagRow
              label={copy.instagram.flagPublicReply}
              value={config.commentPublicReplyEnabled}
            />
            <FlagRow
              label={copy.instagram.flagPrivateReply}
              value={config.commentPrivateReplyEnabled}
            />
            <FlagRow label={copy.instagram.flagDmAutoreply} value={config.dmAutoreplyEnabled} />
            <FlagRow label={copy.instagram.flagBackfillReply} value={config.backfillReplyEnabled} />
            <FlagRow label={copy.instagram.flagReconciler} value={config.reconcilerEnabled} />
            <p className="mt-4 text-xs text-ops-muted">{copy.instagram.deploymentNote}</p>
          </div>

          <div className="ops-card lg:col-span-2">
            <h2 className="mb-2 text-sm font-semibold">{copy.instagram.rulesTitle}</h2>
            {config.commentReplyRules.length === 0 ? (
              <p className="text-sm text-ops-muted">{copy.instagram.noRules}</p>
            ) : (
              config.commentReplyRules.map((rule, index) => (
                <div
                  key={`rule-${index}`}
                  className="mb-4 rounded-xl border border-ops-border/60 p-4 last:mb-0"
                >
                  <p className="text-sm font-medium text-ops-text">
                    {copy.instagram.ruleKeywords}: {rule.keywords.join(', ')}
                  </p>
                  {rule.publicReplyText ? (
                    <p className="mt-2 text-sm text-ops-muted">
                      {copy.instagram.rulePublic}: {rule.publicReplyText}
                    </p>
                  ) : null}
                  <p className="mt-2 text-sm text-ops-muted">
                    {copy.instagram.rulePrivate}:{' '}
                    {rule.hasPrivateReplyText ? copy.instagram.rulePrivateConfigured : copy.common.no}
                  </p>
                  <p className="mt-2 text-sm text-ops-muted">
                    {copy.instagram.ruleAlways}: {formatYesNo(rule.always)}
                  </p>
                </div>
              ))
            )}
            <p className="mt-4 text-xs text-ops-muted">{copy.instagram.keywordsNote}</p>
          </div>

          <div className="ops-card lg:col-span-2">
            <h2 className="mb-2 text-sm font-semibold">{copy.instagram.limitsTitle}</h2>
            <div className="grid gap-0 lg:grid-cols-2">
              <FlagRow
                label={copy.instagram.limitPrivateAge}
                value={config.backfillPrivateReplyMaxAgeDays}
              />
              <FlagRow
                label={copy.instagram.limitBackfillPages}
                value={config.backfillMaxPagesPerRun}
              />
              <FlagRow label={copy.instagram.limitAttempts} value={config.maxReplyAttempts} />
              <FlagRow
                label={copy.instagram.limitRate}
                value={config.rateLimitMessagesPerMinute}
              />
              <FlagRow
                label={copy.instagram.limitReconcilerInterval}
                value={config.reconcilerIntervalMinutes}
              />
              <FlagRow label={copy.instagram.limitReconcilerMedia} value={config.reconcilerMediaLimit} />
              <FlagRow
                label={copy.instagram.limitReconcilerPages}
                value={config.reconcilerMaxCommentPagesPerMedia}
              />
              <FlagRow
                label={copy.instagram.limitReconcilerLookback}
                value={config.reconcilerLookbackHours}
              />
            </div>
          </div>
        </div>
      ) : null}

      {setupQuery.data?.configured && tab === 'settings' && statusQuery.isLoading ? (
        <LoadingState label={copy.instagram.loadingStatus} />
      ) : null}

      {setupQuery.data?.configured && tab === 'operations' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <form className="ops-card space-y-4" onSubmit={handleBackfill}>
            <div>
              <h2 className="text-sm font-semibold">{copy.instagram.backfillTitle}</h2>
              <p className="mt-1 text-sm text-ops-muted">{copy.instagram.backfillDescription}</p>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">{copy.instagram.mediaIdLabel}</span>
              <input
                className="ops-input"
                value={mediaId}
                onChange={(event) => setMediaId(event.target.value)}
                placeholder="17865092364637923"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ops-text">
              <input
                type="checkbox"
                checked={backfillSend}
                onChange={(event) => setBackfillSend(event.target.checked)}
              />
              {copy.instagram.backfillSend}
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">{copy.instagram.backfillAfter}</span>
              <input
                className="ops-input"
                value={backfillAfter}
                onChange={(event) => setBackfillAfter(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">{copy.instagram.backfillMaxPages}</span>
              <input
                className="ops-input"
                value={backfillMaxPages}
                onChange={(event) => setBackfillMaxPages(event.target.value)}
              />
            </label>
            <button type="submit" className="ops-btn-primary" disabled={backfillMutation.isPending}>
              {backfillMutation.isPending ? copy.instagram.running : copy.instagram.backfillSubmit}
            </button>
          </form>

          <form className="ops-card space-y-4" onSubmit={handleReply}>
            <div>
              <h2 className="text-sm font-semibold">{copy.instagram.replyTitle}</h2>
              <p className="mt-1 text-sm text-ops-muted">{copy.instagram.replyDescription}</p>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">{copy.instagram.commentIdLabel}</span>
              <input
                className="ops-input"
                value={commentId}
                onChange={(event) => setCommentId(event.target.value)}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ops-text">
              <input
                type="checkbox"
                checked={replyForce}
                onChange={(event) => setReplyForce(event.target.checked)}
              />
              {copy.instagram.replyForce}
            </label>
            <button type="submit" className="ops-btn-primary" disabled={replyMutation.isPending}>
              {replyMutation.isPending ? copy.instagram.running : copy.instagram.replySubmit}
            </button>
          </form>

          {actionMessage ? (
            <div className="ops-card border-ops-success/20 bg-ops-success-soft text-sm text-ops-success lg:col-span-2">
              {actionMessage}
            </div>
          ) : null}
          {actionError ? (
            <div className="ops-card border-ops-danger/20 bg-ops-danger-soft text-sm text-ops-danger lg:col-span-2">
              {actionError}
            </div>
          ) : null}
          {subjectReport && tab === 'operations' ? (
            <div className="ops-card lg:col-span-2">
              <h2 className="mb-3 text-sm font-semibold">{copy.instagram.lastResult}</h2>
              <pre className="overflow-x-auto rounded-xl bg-ops-surface p-4 text-xs text-ops-secondary">
                {JSON.stringify(subjectReport, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}

      {setupQuery.data?.configured && tab === 'setup' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="ops-card">
            <h2 className="mb-3 text-sm font-semibold">{copy.instagram.setupWorkerTitle}</h2>
            <KeyValueList
              items={[
                {
                  label: copy.instagram.setupWorkerUrl,
                  value: setupQuery.data.admin_base_url ?? copy.common.notAvailable,
                },
                {
                  label: copy.instagram.setupHealth,
                  value:
                    healthQuery.data && typeof healthQuery.data.ok === 'boolean'
                      ? healthQuery.data.ok
                        ? copy.instagram.healthOk
                        : copy.instagram.healthBad
                      : healthQuery.isLoading
                        ? copy.common.loading
                        : copy.common.notAvailable,
                },
                {
                  label: copy.instagram.setupWebhookPath,
                  value: '/webhooks/instagram',
                },
              ]}
            />
            {config?.publicBaseUrl ? (
              <a
                href={`${config.publicBaseUrl}/webhooks/instagram`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-ops-accent"
              >
                {copy.instagram.openWebhookUrl}
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : null}
          </div>

          <div className="ops-card">
            <h2 className="mb-3 text-sm font-semibold">{copy.instagram.setupMetaTitle}</h2>
            <ul className="space-y-3 text-sm text-ops-muted">
              {copy.instagram.setupMetaChecklist.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="text-ops-accent">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="ops-card lg:col-span-2">
            <h2 className="mb-3 text-sm font-semibold">{copy.instagram.setupNotesTitle}</h2>
            <ul className="space-y-2 text-sm text-ops-muted">
              {copy.instagram.setupNotes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {setupQuery.data?.configured && tab === 'privacy' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <form className="ops-card space-y-4" onSubmit={handleLookup}>
            <div>
              <h2 className="text-sm font-semibold">{copy.instagram.privacyTitle}</h2>
              <p className="mt-1 text-sm text-ops-muted">{copy.instagram.privacyDescription}</p>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">commentId</span>
              <input
                className="ops-input"
                value={subjectCommentId}
                onChange={(event) => setSubjectCommentId(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">directMessageId</span>
              <input
                className="ops-input"
                value={subjectDirectMessageId}
                onChange={(event) => setSubjectDirectMessageId(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">commenterId</span>
              <input
                className="ops-input"
                value={subjectCommenterId}
                onChange={(event) => setSubjectCommenterId(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ops-muted">username</span>
              <input
                className="ops-input"
                value={subjectUsername}
                onChange={(event) => setSubjectUsername(event.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="submit" className="ops-btn-secondary" disabled={lookupMutation.isPending}>
                {lookupMutation.isPending ? copy.instagram.running : copy.instagram.lookupSubmit}
              </button>
              <button
                type="button"
                className="ops-btn-primary"
                disabled={redactMutation.isPending}
                onClick={handleRedact}
              >
                {redactMutation.isPending ? copy.instagram.running : copy.instagram.redactSubmit}
              </button>
            </div>
          </form>

          {actionMessage ? (
            <div className="ops-card border-ops-success/20 bg-ops-success-soft text-sm text-ops-success">
              {actionMessage}
            </div>
          ) : null}
          {actionError ? (
            <div className="ops-card border-ops-danger/20 bg-ops-danger-soft text-sm text-ops-danger">
              {actionError}
            </div>
          ) : null}

          {subjectReport && tab === 'privacy' ? (
            <div className="ops-card lg:col-span-2">
              <h2 className="mb-3 text-sm font-semibold">{copy.instagram.privacyReport}</h2>
              <pre className="overflow-x-auto rounded-xl bg-ops-surface p-4 text-xs text-ops-secondary">
                {JSON.stringify(subjectReport, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
